import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useQueryClient } from '@zg-rentals/react-query5';

import { Flex } from '@zillow/constellation';
import { FormField, Text, TextButton } from '@zillow/constellation-10';
import type { TextPropsInterface } from '@zillow/constellation-10';
import { debounce } from '@zg-rentals/zdash';
import { cloneDeep } from 'es-toolkit';

import { useSelector } from '../../selectors/useSelector';

import SchedulerActions from '../../actions/SchedulerActions';

import { GET_LISTING_RECS } from '../../constants/RecommendationsConstants';
import TimeConstants from '../../constants/TimeConstants';
import stringHelper from '../../utils/stringHelper';

import { StyledInlineSpacer, StyledSchedulerButton } from './StyledComponents';

import type { DayOfTheWeekProps, ScheduleProps } from '../../model/Schedule';

type Props = {
  textStyle?: TextPropsInterface['textStyle'];
  hideLabel?: boolean;
  hideTitle?: boolean;
  isInputFormDisabled?: boolean;
  onError?: (message: string) => void;
  schedule: ScheduleProps;
  updateFormData: (data: { schedule: ScheduleProps }) => void;
  updateFetchingStatus: (isFetching: boolean) => void;
};

type ScheduleResponse = {
  schedule: ScheduleProps;
};

const Scheduler = ({
  textStyle = 'heading-xs',
  hideLabel = false,
  hideTitle = false,
  isInputFormDisabled = false,
  onError = () => {},
  schedule,
  updateFormData,
  updateFetchingStatus,
}: Props) => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  const {
    properties: {
      currentProperty: { listingAlias },
    },
  } = useSelector((state) => state);

  const [scheduleState, setScheduleState] = useState(schedule);

  const initialRender = useRef(true);

  const allSelected = scheduleState.every(({ available }: DayOfTheWeekProps) => available);

  useEffect(() => {
    if (initialRender.current) {
      initialRender.current = false;
      return;
    }

    saveScheduleDataDebounced(scheduleState);
  }, [scheduleState]);

  const saveScheduleData = (scheduleData: ScheduleProps) => {
    updateFetchingStatus(true);

    return (
      dispatch(SchedulerActions.update(listingAlias, { schedule: scheduleData }))
        // @ts-expect-error
        .then((res: ScheduleResponse) => {
          updateFormData({ schedule: res.schedule });
          queryClient.invalidateQueries({ queryKey: [GET_LISTING_RECS, listingAlias] });
        })
        .catch(() => {
          const message = 'There was an issue saving your changes.';

          onError(message);
        })
        .finally(() => {
          updateFetchingStatus(false);
        })
    );
  };

  const saveScheduleDataDebounced = useCallback(
    debounce(saveScheduleData, TimeConstants.SELECTION_DEBOUNCE_TIME_MS),
    [],
  );

  const handleTimeOfDaySelect = (index: number) => {
    const scheduleCopy = cloneDeep(scheduleState);
    const dayToChange = scheduleCopy[index];
    dayToChange.available = !dayToChange.available;

    setScheduleState(scheduleCopy);
  };

  const handleSelectAll = () => {
    let scheduleCopy = cloneDeep(scheduleState);
    scheduleCopy = scheduleCopy.map(({ day }: DayOfTheWeekProps) => ({ day, available: !allSelected }));

    setScheduleState(scheduleCopy);
  };

  return (
    <div className="Scheduler">
      {!hideTitle && (
        <Flex display="flex" alignItems="center">
          <Text textStyle={textStyle} asChild>
            <p>Tour availability</p>
          </Text>
        </Flex>
      )}
      {scheduleState.length < 1 ? (
        <Text>Sorry, the schedule service is currently unavailable. Please try again later.</Text>
      ) : (
        <>
          <div role="group" aria-labelledby="select-availability">
            {!hideLabel && <FormField>Select your availability.</FormField>}
            <Flex data-testid="Scheduler-day-item" as="ul" display="flex" flexDirection="row" flexWrap="wrap" gap="0">
              {scheduleState.map(({ day, available }: DayOfTheWeekProps, index: number) => (
                <StyledInlineSpacer as="li" key={`scheduler-button-spacer-${index}`} marginRight="xs">
                  <StyledSchedulerButton
                    data-testid="scheduler-button"
                    buttonType="tertiary"
                    key={`scheduler-button-${index}`}
                    disabled={isInputFormDisabled}
                    pressed={available}
                    onClick={() => handleTimeOfDaySelect(index)}
                  >
                    {stringHelper.capitalize(`${day}s`)}
                  </StyledSchedulerButton>
                </StyledInlineSpacer>
              ))}
            </Flex>
            <TextButton
              css={{ marginTop: '400' }}
              tone="brand"
              onClick={handleSelectAll}
              data-testid="Scheduler-SelectAllButton"
            >
              {allSelected ? 'Deselect all' : 'Select all'}
            </TextButton>
          </div>
        </>
      )}
    </div>
  );
};

export default Scheduler;
